import { IHistoryAuthorTagProps } from '@src/slide/application/service/history-manager/core/components/author-tag';
import { SubscribableService } from '@src/slide/application/service/subscribable-service';
import { EventMap } from '@src/slide/infrastructure/module/independent/event-emitter/event-emitter.interface';

export interface IHistoryManager extends SubscribableService<IHistoryEventMap> {
    // 是否允许修改编辑器状态
    canUpdateEditorState: boolean;
    isShowHistoryPanel: boolean;
    changedComponents: any[];
    isShowingHistory: boolean;
    modification: any;
    sectionModification: any;
    recordType?: string;
    showModule(DOMNode: Element, onShow?: () => void, onClose?: () => void): void;
    hideModule(DOMNode: Element): void;
    renderHistoryLoadingMask(): React.ReactElement;
    renderHistoryAuthorTag(props: IHistoryAuthorTagProps): React.ReactElement;
    renderSnapshotRecovery(): React.ReactElement;
    getColorForId(id: any): any;
    initHistoryRevertListeners(): void;
    isShown(): boolean;
    show(config?: Partial<IShowHistoryConfig>): void;
    mobileShow(config?: Partial<IShowHistoryConfig>): void;
    getAuthor(id: any): any;
}

export type IHistoryManagerInjectable = {
    enableHistoryVersion: boolean;
    isSupportArkVersionManager: boolean;
    onBeforeRevertUrlReload(): boolean;
    listenStatusChange?(): void;
    versionPreviewInvoke?(configs?: IShowHistoryConfig): void;
    deleteSearchParamNotAlreadyCreate(): void;
} & Partial<Pick<IShowRecordComponentConfig, 'defaultTab'>> &
    Pick<IShowRecordComponentConfig, 'onVersionRevert' | 'onVersionSelect'>;

interface Author {
    /** 名称 */
    name: string;
    /** 用户头像 */
    userPic: string;
}

interface VersionItem {
    /** 版本号 */
    v: number;
    /** 记录 ID */
    recordId: number;
    /** revisionId */
    rev: number;
    /** 时间戳 */
    time: number;
    /** 创建者 */
    creator: string;
    /** 版本别名 */
    alias: string;
}

interface EditRecordItem {
    /** 作者 */
    a: string[];
    /** 数据版本 */
    d: string | number[];
    /** revisionId */
    i: number;
    /** 子记录 */
    l: {
        /** 子记录作者 */
        a: string;
        /** 子记录 revisionId */
        i: number;
        /** 子记录类型 */
        t: number;
    }[];
    /** 时间戳 */
    t: number;
    /** 版本号 */
    v: number;
    /** 回滚记录 */
    r?: string;
    /** 启始 revisionId */
    s?: number;
    /** 是否升级记录 */
    isUpgrade?: boolean;
    /** 额外信息 */
    ext?: Record<string, any>;
    /** 回滚类型，目前只有私有化用到。0是正常编辑和revert，1是上传新版本 */
    rev_type?: number;
}

export interface OnEditRecordSelectPayload {
    /** 当前修改 */
    rev: number;
    /** 前置修改 */
    fRev?: number;
    /** 是否为回滚版本 */
    isRevertedVersion?: boolean;
    historyItem: EditRecordItem;
    /** subItemIndex 有值时为小版本 */
    subItemIndex?: number;
    /** 作者信息映射 */
    authors: Map<string, Author>;
}

export interface OnEditRecordRevertPayload {
    /** 只有大版本的时候才会带上 v 参数 */
    v?: number;
    rev: number;
    historyItem: EditRecordItem;
    subItemIndex?: number;
}

export interface OnVersionSelectPayload {
    recordId: number;
    rev: number;
    versionItem: VersionItem;
}

export interface OnVersionRevertPayload {
    recordId: number;
    rev: number;
    versionItem: VersionItem;
}

export interface IShowRecordComponentConfig {
    defaultTab: string;
    targetDom: any;
    onClose: Function;
    /** 选择修订记录时的回调函数 */
    onEditRecordSelect?: (onSelectPayload: OnEditRecordSelectPayload) => void;
    /** 还原修订记录时的回调函数 */
    onEditRecordRevert?: (onRevertPayload: OnEditRecordRevertPayload) => void;
    /** 还原修订记录成功时的回调函数 */
    onEditRecordRevertSuccess?: (payload: OnEditRecordRevertPayload) => unknown;
    /** 选择版本时的回调函数 */
    onVersionSelect?: (payload: OnVersionSelectPayload) => void;
    /** 版本回滚的回调函数 */
    onVersionRevert?: (payload: OnVersionRevertPayload) => void;
}

export interface IShowHistoryConfig {
    activeId: string;
    showTab: boolean;
    type: string;
}

export enum HistoryEventType {
    REVERT_BEGIN = 'revertBegin',
    REVERT_END = 'revertEnd',
    SHOW = 'show',
    HIDE = 'hide',
    FETCH_BEGIN = 'fetchBegin',
    FETCH_END = 'fetchEnd',
    FETCHED = 'fetched',
}

export interface IHistoryEventMap extends EventMap {
    [HistoryEventType.REVERT_BEGIN]: any;
    [HistoryEventType.REVERT_END]: any;
    [HistoryEventType.SHOW]: undefined;
    [HistoryEventType.HIDE]: any;
    [HistoryEventType.FETCH_BEGIN]: any;
    [HistoryEventType.FETCH_END]: any;
    [HistoryEventType.FETCHED]: undefined;
}
